import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useFeedStore } from '@/stores/feed';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';
import { PostComposer } from '@/components/PostComposer';
import { PostCard } from '@/components/PostCard';
import { RightSidebar } from '@/components/RightSidebar';
import { useHiStore } from '@/stores/hi';
import type {
  UserProfile,
  ActivityLogEntry,
  GiftWithDetails,
  GiftType,
  Album,
} from '@shared/types';

type ProfileTab = 'wall' | 'info' | 'photos' | 'friends';

interface FriendCard {
  id: string;
  display_name: string;
  username: string;
  avatar_key: string | null;
  neighborhood: string | null;
}

export function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [searchParams] = useSearchParams();
  const { user: me } = useAuthStore();
  const { posts, loading, hasMore, fetchUserPosts } = useFeedStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [hiSent, setHiSent] = useState(false);
  const { sendHi } = useHiStore();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<ProfileTab>('wall');

  // Wall tab state
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [gifts, setGifts] = useState<GiftWithDetails[]>([]);

  // Info tab state
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({
    work: '',
    education: '',
    relationship_status: '',
    hometown: '',
    gender: '',
    interests: '',
    favorite_quotes: '',
    political: '',
    religious: '',
  });
  const [savingInfo, setSavingInfo] = useState(false);

  // Photos tab state
  const [albums, setAlbums] = useState<Album[]>([]);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDesc, setNewAlbumDesc] = useState('');

  // Friends tab state
  const [friends, setFriends] = useState<FriendCard[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);

  // Gift modal state
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftTypes, setGiftTypes] = useState<GiftType[]>([]);
  const [selectedGift, setSelectedGift] = useState<string | null>(null);
  const [giftMessage, setGiftMessage] = useState('');
  const [sendingGift, setSendingGift] = useState(false);
  const [giftSent, setGiftSent] = useState(false);

  // Birthday pre-fill
  const isBirthdayPost = searchParams.get('birthday') === '1';

  useEffect(() => {
    if (!username) return;
    setLoadingProfile(true);
    setHiSent(false);
    setActiveTab('wall');
    setEditingInfo(false);
    api.get<UserProfile>(`/users/${username}`)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoadingProfile(false));
    fetchUserPosts(username, true);
  }, [username]);

  // Fetch wall extras when on wall tab
  useEffect(() => {
    if (!username || activeTab !== 'wall') return;
    api.get<ActivityLogEntry[]>(`/users/${username}/activity`).then(setActivities).catch(() => {});
    api.get<GiftWithDetails[]>(`/users/${username}/gifts`).then(setGifts).catch(() => {});
  }, [username, activeTab]);

  // Fetch albums when on photos tab
  useEffect(() => {
    if (!username || activeTab !== 'photos') return;
    api.get<Album[]>(`/users/${username}/albums`).then(setAlbums).catch(() => {});
  }, [username, activeTab]);

  // Fetch friends when on friends tab
  useEffect(() => {
    if (!username || activeTab !== 'friends') return;
    setLoadingFriends(true);
    api.get<FriendCard[]>(`/users/${username}/friends`)
      .then(setFriends)
      .catch(() => {})
      .finally(() => setLoadingFriends(false));
  }, [username, activeTab]);

  // Populate info form when profile loads
  useEffect(() => {
    if (!profile) return;
    setInfoForm({
      work: profile.work || '',
      education: profile.education || '',
      relationship_status: profile.relationship_status || '',
      hometown: profile.hometown || '',
      gender: profile.gender || '',
      interests: profile.interests || '',
      favorite_quotes: profile.favorite_quotes || '',
      political: profile.political || '',
      religious: profile.religious || '',
    });
  }, [profile]);

  const handleFriendAction = async () => {
    if (!profile) return;
    setActionLoading(true);
    try {
      if (!profile.connection_status) {
        await api.post('/connections/request', { user_id: profile.id });
        setProfile({ ...profile, connection_status: 'pending' });
      } else if (profile.connection_status === 'pending' && profile.connection_id) {
        await api.post(`/connections/${profile.connection_id}/accept`);
        setProfile({ ...profile, connection_status: 'accepted', friend_count: profile.friend_count + 1 });
      } else if (profile.connection_status === 'accepted' && profile.connection_id) {
        await api.del(`/connections/${profile.connection_id}`);
        setProfile({ ...profile, connection_status: null, connection_id: null, friend_count: profile.friend_count - 1 });
      }
    } catch (e: any) {
      alert(e.message || 'Something went wrong');
    }
    setActionLoading(false);
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    const formData = new FormData();
    formData.append('cover', file);
    try {
      const result = await api.upload<{ cover_key: string }>('/users/me/cover', formData);
      setProfile({ ...profile, cover_key: result.cover_key });
    } catch {
      // ignore
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const result = await api.upload<{ avatar_key: string }>('/users/me/avatar', formData);
      setProfile({ ...profile, avatar_key: result.avatar_key });
    } catch {
      // ignore
    }
  };

  const handleSaveInfo = async () => {
    if (!profile) return;
    setSavingInfo(true);
    try {
      await api.put('/users/me', infoForm);
      setProfile({
        ...profile,
        work: infoForm.work || null,
        education: infoForm.education || null,
        relationship_status: infoForm.relationship_status || null,
        hometown: infoForm.hometown || null,
        gender: infoForm.gender || null,
        interests: infoForm.interests || null,
        favorite_quotes: infoForm.favorite_quotes || null,
        political: infoForm.political || null,
        religious: infoForm.religious || null,
      });
      setEditingInfo(false);
    } catch {
      // ignore
    }
    setSavingInfo(false);
  };

  const handleCreateAlbum = async () => {
    if (!newAlbumName.trim()) return;
    try {
      const album = await api.post<Album>('/albums', { name: newAlbumName.trim(), description: newAlbumDesc.trim() });
      setAlbums([album, ...albums]);
      setNewAlbumName('');
      setNewAlbumDesc('');
      setCreatingAlbum(false);
    } catch {
      // ignore
    }
  };

  const handleSendGift = async () => {
    if (!profile || !selectedGift) return;
    setSendingGift(true);
    try {
      await api.post('/gifts', {
        recipient_id: profile.id,
        gift_type_id: selectedGift,
        message: giftMessage.trim(),
      });
      setGiftSent(true);
      setTimeout(() => {
        setShowGiftModal(false);
        setGiftSent(false);
        setSelectedGift(null);
        setGiftMessage('');
      }, 1500);
    } catch {
      // ignore
    }
    setSendingGift(false);
  };

  const openGiftModal = async () => {
    setShowGiftModal(true);
    if (giftTypes.length === 0) {
      try {
        const types = await api.get<GiftType[]>('/gifts/types');
        setGiftTypes(types);
      } catch {
        // ignore
      }
    }
  };

  const friendButtonText = () => {
    if (!profile?.connection_status) return '+ Add Friend';
    if (profile.connection_status === 'pending') return '\u23f3 Pending';
    if (profile.connection_status === 'accepted') return '\u2713 Friends';
    return '+ Add Friend';
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const then = new Date(dateStr + 'Z');
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
  };

  if (loadingProfile) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="animate-pulse text-3xl mb-2">{'\u301c\ufe0f'}</div>
        <p className="text-sm">Tuning in {'\u223f'}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-5xl mx-auto px-3 py-8 text-center text-sand-500">
        <div className="text-3xl mb-2">{'\ud83e\udd37'}</div>
        <p className="text-sm">That person doesn't exist here</p>
      </div>
    );
  }

  const isMe = profile.is_me;
  const isFriend = profile.connection_status === 'accepted';

  const tabs: { key: ProfileTab; label: string }[] = [
    { key: 'wall', label: 'Wall' },
    { key: 'info', label: 'Info' },
    { key: 'photos', label: 'Photos' },
    { key: 'friends', label: 'Friends' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-3 py-4">
      {/* Cover photo + profile header */}
      <div className="card mb-4 overflow-hidden">
        {/* Cover */}
        <div className="relative h-48 bg-gradient-to-br from-vibe-300 to-vibe-500">
          {profile.cover_key && (
            <img
              src={mediaUrl(profile.cover_key)}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
          {isMe && (
            <>
              <button
                onClick={() => coverInputRef.current?.click()}
                className="absolute bottom-2 right-2 bg-black/50 text-white text-[10px] px-2 py-1 rounded hover:bg-black/70 transition-colors z-10"
              >
                &#128247; Edit Cover
              </button>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCoverUpload}
                className="hidden"
              />
            </>
          )}
        </div>

        {/* Profile info */}
        <div className="px-4 pb-4 relative">
          <div className="flex items-end gap-3 -mt-10 mb-3">
            <div className="relative shrink-0">
              <img
                src={mediaUrl(profile.avatar_key)}
                alt=""
                className="w-24 h-24 rounded-lg object-cover border-4 border-white shadow-md"
              />
              {isMe && (
                <>
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    className="absolute inset-0 rounded-lg bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <span className="text-white text-xs font-bold">Edit</span>
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                </>
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-xl font-bold text-sand-800 truncate">{profile.display_name}</h1>
              <p className="text-sand-500 text-xs">@{profile.username}</p>
            </div>
            {!isMe && (
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={handleFriendAction}
                  disabled={actionLoading}
                  className={`text-xs font-bold py-1.5 px-4 rounded-md transition-colors ${
                    isFriend
                      ? 'bg-sand-200 text-sand-600 hover:bg-red-100 hover:text-red-600'
                      : 'btn-primary'
                  } disabled:opacity-40`}
                >
                  {friendButtonText()}
                </button>
                <button
                  onClick={async () => {
                    if (hiSent) return;
                    try {
                      await sendHi(profile.id);
                      setHiSent(true);
                    } catch {}
                  }}
                  disabled={hiSent}
                  className="text-xs font-bold py-1.5 px-4 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors disabled:opacity-40"
                >
                  {hiSent ? 'Hi sent!' : '\ud83d\udc4b Hi'}
                </button>
                <button
                  onClick={openGiftModal}
                  className="text-xs font-bold py-1.5 px-4 rounded-md bg-pink-100 text-pink-700 hover:bg-pink-200 transition-colors"
                >
                  &#127873; Gift
                </button>
              </div>
            )}
          </div>

          {profile.bio && <p className="text-sm text-sand-700 mb-2">{profile.bio}</p>}

          <div className="flex gap-4 text-xs text-sand-500">
            <span><span className="font-bold text-sand-700">{profile.friend_count}</span> friends</span>
            {profile.neighborhood && <span>&#128205; {profile.neighborhood}</span>}
            {profile.birthday && <span>&#127874; {profile.birthday}</span>}
            <span>Joined {new Date(profile.created_at + 'Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-sand-300 flex">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 text-xs font-bold text-center transition-colors ${
                activeTab === tab.key
                  ? 'text-vibe-600 border-b-2 border-vibe-500 bg-sand-50'
                  : 'text-sand-500 hover:text-sand-700 hover:bg-sand-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex gap-4">
        <div className="flex-1 min-w-0">
          {/* ===== WALL TAB ===== */}
          {activeTab === 'wall' && (
            <>
              {/* Recent Activity */}
              {activities.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header">&#9889; Recent Activity</div>
                  <div className="p-3 space-y-2">
                    {activities.slice(0, 5).map((a) => (
                      <div key={a.id} className="flex items-start gap-2">
                        <Link to={`/profile/${a.user_username}`} className="shrink-0">
                          <img src={mediaUrl(a.user_avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
                        </Link>
                        <div className="text-xs text-sand-600">
                          <Link to={`/profile/${a.user_username}`} className="link font-bold">
                            {a.user_display_name}
                          </Link>{' '}
                          {a.type.replace(/_/g, ' ')}
                          {a.target_display_name && a.target_username && (
                            <>
                              {' '}
                              <Link to={`/profile/${a.target_username}`} className="link font-bold">
                                {a.target_display_name}
                              </Link>
                            </>
                          )}
                          <span className="text-sand-400 ml-1">{timeAgo(a.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gifts received */}
              {gifts.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header">&#127873; Gifts</div>
                  <div className="p-3 space-y-2">
                    {gifts.slice(0, 5).map((g) => (
                      <div key={g.id} className="flex items-center gap-2">
                        <span className="text-2xl">{g.gift_emoji}</span>
                        <div className="text-xs text-sand-600 flex-1">
                          <Link to={`/profile/${g.sender_username}`} className="link font-bold">
                            {g.sender_display_name}
                          </Link>{' '}
                          sent a <span className="font-semibold">{g.gift_name}</span>
                          {g.message && <span className="text-sand-500"> &mdash; "{g.message}"</span>}
                        </div>
                        <span className="text-[10px] text-sand-400">{timeAgo(g.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Wall composer */}
              {(isMe || isFriend) && (
                <PostComposer
                  wallOwnerId={profile.id}
                  wallOwnerName={isMe ? undefined : profile.display_name}
                  onPostCreated={() => username && fetchUserPosts(username, true)}
                  defaultContent={isBirthdayPost ? 'Happy Birthday! \ud83c\udf89' : undefined}
                />
              )}

              {/* Wall posts */}
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
              {loading && <p className="text-center text-sand-500 text-xs py-4">Catching waves {'\u223f'}</p>}
              {hasMore && !loading && posts.length > 0 && (
                <button onClick={() => username && fetchUserPosts(username)} className="btn-secondary w-full text-xs">
                  Earlier posts
                </button>
              )}
              {!loading && posts.length === 0 && (
                <div className="text-center py-8 text-sand-500">
                  <div className="text-3xl mb-2">{'\u301c\ufe0f'}</div>
                  <p className="text-sm">This wall is wide open. Be the first to write on it {'\u223f'}</p>
                </div>
              )}
            </>
          )}

          {/* ===== INFO TAB ===== */}
          {activeTab === 'info' && (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <span>&#128196; Information</span>
                {isMe && !editingInfo && (
                  <button onClick={() => setEditingInfo(true)} className="text-[10px] text-vibe-500 hover:text-vibe-600 font-bold">
                    Edit Info
                  </button>
                )}
              </div>
              <div className="p-4">
                {editingInfo ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Work</label>
                      <input value={infoForm.work} onChange={(e) => setInfoForm({ ...infoForm, work: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Education</label>
                      <input value={infoForm.education} onChange={(e) => setInfoForm({ ...infoForm, education: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Relationship Status</label>
                      <select value={infoForm.relationship_status} onChange={(e) => setInfoForm({ ...infoForm, relationship_status: e.target.value })} className="input w-full text-xs">
                        <option value="">-- Select --</option>
                        <option value="single">Single</option>
                        <option value="in a relationship">In a Relationship</option>
                        <option value="engaged">Engaged</option>
                        <option value="married">Married</option>
                        <option value="it's complicated">It's Complicated</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Hometown</label>
                      <input value={infoForm.hometown} onChange={(e) => setInfoForm({ ...infoForm, hometown: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Gender</label>
                      <input value={infoForm.gender} onChange={(e) => setInfoForm({ ...infoForm, gender: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Interests</label>
                      <textarea value={infoForm.interests} onChange={(e) => setInfoForm({ ...infoForm, interests: e.target.value })} className="input w-full text-xs resize-none" rows={2} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Favorite Quotes</label>
                      <textarea value={infoForm.favorite_quotes} onChange={(e) => setInfoForm({ ...infoForm, favorite_quotes: e.target.value })} className="input w-full text-xs resize-none" rows={2} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Political Views</label>
                      <input value={infoForm.political} onChange={(e) => setInfoForm({ ...infoForm, political: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-sand-500 uppercase mb-1">Religious Views</label>
                      <input value={infoForm.religious} onChange={(e) => setInfoForm({ ...infoForm, religious: e.target.value })} className="input w-full text-xs" />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button onClick={handleSaveInfo} disabled={savingInfo} className="btn-primary text-xs py-1.5 px-4 disabled:opacity-40">
                        {savingInfo ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => setEditingInfo(false)} className="btn-secondary text-xs py-1.5 px-4">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {[
                        { label: 'Work', value: profile.work },
                        { label: 'Education', value: profile.education },
                        { label: 'Relationship Status', value: profile.relationship_status },
                        { label: 'Hometown', value: profile.hometown },
                        { label: 'Gender', value: profile.gender },
                        { label: 'Interests', value: profile.interests },
                        { label: 'Favorite Quotes', value: profile.favorite_quotes },
                        { label: 'Political Views', value: profile.political },
                        { label: 'Religious Views', value: profile.religious },
                        { label: 'Neighborhood', value: profile.neighborhood },
                        { label: 'Birthday', value: profile.birthday },
                      ].map(({ label, value }) => (
                        <tr key={label} className="border-b border-sand-200 last:border-0">
                          <td className="py-2 pr-4 font-bold text-sand-500 w-36 align-top">{label}</td>
                          <td className="py-2 text-sand-800 whitespace-pre-wrap">{value || <span className="text-sand-400 italic">Not specified</span>}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-2 pr-4 font-bold text-sand-500 w-36">Member Since</td>
                        <td className="py-2 text-sand-800">
                          {new Date(profile.created_at + 'Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ===== PHOTOS TAB ===== */}
          {activeTab === 'photos' && (
            <div>
              {isMe && (
                <div className="card mb-3">
                  {creatingAlbum ? (
                    <div className="p-3 space-y-2">
                      <input
                        value={newAlbumName}
                        onChange={(e) => setNewAlbumName(e.target.value)}
                        placeholder="Album name"
                        className="input w-full text-xs"
                      />
                      <input
                        value={newAlbumDesc}
                        onChange={(e) => setNewAlbumDesc(e.target.value)}
                        placeholder="Description (optional)"
                        className="input w-full text-xs"
                      />
                      <div className="flex gap-2">
                        <button onClick={handleCreateAlbum} className="btn-primary text-xs py-1 px-3">Create</button>
                        <button onClick={() => setCreatingAlbum(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3">
                      <button onClick={() => setCreatingAlbum(true)} className="btn-primary text-xs py-1.5 px-4">
                        + Create Album
                      </button>
                    </div>
                  )}
                </div>
              )}

              {albums.length === 0 ? (
                <div className="card p-6 text-center text-sand-500">
                  <div className="text-3xl mb-2">&#128247;</div>
                  <p className="text-sm">No albums yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {albums.map((album) => (
                    <Link key={album.id} to={`/albums/${album.id}`} className="card hover:shadow-md transition-shadow overflow-hidden">
                      <div className="h-32 bg-sand-200 flex items-center justify-center text-3xl text-sand-400">
                        &#128247;
                      </div>
                      <div className="p-2">
                        <div className="text-xs font-bold text-sand-800 truncate">{album.name}</div>
                        {album.description && (
                          <div className="text-[10px] text-sand-500 truncate">{album.description}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== FRIENDS TAB ===== */}
          {activeTab === 'friends' && (
            <div>
              {loadingFriends ? (
                <p className="text-center text-sand-500 text-xs py-4">Loading friends...</p>
              ) : friends.length === 0 ? (
                <div className="card p-6 text-center text-sand-500">
                  <div className="text-3xl mb-2">&#128101;</div>
                  <p className="text-sm">No friends to show yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {friends.map((friend) => (
                    <Link key={friend.id} to={`/profile/${friend.username}`} className="card hover:shadow-md transition-shadow p-3 flex items-center gap-2">
                      <img src={mediaUrl(friend.avatar_key)} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-sand-800 truncate">{friend.display_name}</div>
                        {friend.neighborhood && (
                          <div className="text-[10px] text-sand-500 truncate">&#128205; {friend.neighborhood}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {activeTab === 'wall' && <RightSidebar />}
      </div>

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowGiftModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="card-header flex items-center justify-between">
              <span>&#127873; Send a Gift to {profile.display_name}</span>
              <button onClick={() => setShowGiftModal(false)} className="text-sand-400 hover:text-sand-600">&#10005;</button>
            </div>
            <div className="p-4">
              {giftSent ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-2">&#127873;</div>
                  <p className="text-sm font-bold text-vibe-600">Gift sent!</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {giftTypes.map((gt) => (
                      <button
                        key={gt.id}
                        onClick={() => setSelectedGift(gt.id)}
                        className={`p-3 rounded border text-center transition-colors ${
                          selectedGift === gt.id
                            ? 'border-vibe-500 bg-vibe-50'
                            : 'border-sand-300 hover:border-sand-400'
                        }`}
                      >
                        <div className="text-2xl mb-1">{gt.emoji}</div>
                        <div className="text-[10px] font-bold text-sand-700">{gt.name}</div>
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={giftMessage}
                    onChange={(e) => setGiftMessage(e.target.value)}
                    placeholder="Add a message (optional)"
                    className="input w-full text-xs resize-none mb-3"
                    rows={2}
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowGiftModal(false)} className="btn-secondary text-xs py-1.5 px-4">Cancel</button>
                    <button
                      onClick={handleSendGift}
                      disabled={!selectedGift || sendingGift}
                      className="btn-primary text-xs py-1.5 px-4 disabled:opacity-40"
                    >
                      {sendingGift ? 'Sending...' : 'Send Gift'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
